/**
 * Seconds-based adapter that keeps the VidStack-shaped `window.player` surface
 * (src objects, state.playing, subscribe, media events) on top of a Video.js 10
 * `<video-player>` and whichever media element is playing the current source.
 *
 * MediaPlayer.svelte renders the media element for `adapter.kind` and hands it
 * over with `attachMedia`; everything else in the app talks to the adapter.
 */

import { updatePlayerSpeed } from "@app/store/playerStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { externalDataState } from "@app/store/externalDataStore.svelte.js";
import { detectMediaKind, MEDIA_KIND_TAGS, MEDIA_SOURCES } from "./mediaSources.js";

/** Events re-dispatched from the media element so existing listeners keep working. */
const FORWARDED_EVENTS = [
    "loadstart",
    "loadedmetadata",
    "canplay",
    "durationchange",
    "play",
    "playing",
    "pause",
    "waiting",
    "ended",
    "timeupdate",
    "seeking",
    "seeked",
    "ratechange",
    "volumechange",
    "error",
];

/** Engines that are only needed for less common sources load on first use. */
const LAZY_MEDIA = {
    dash: () => import("@videojs/html/media/dash-video"),
    cloudflare: () => import("@videojs/html/media/cloudflare-video"),
};

/** Embedded players reject playback rates outside their own range. */
const RATE_LIMITS = {
    youtube: [0.25, 2],
    vimeo: [0.5, 2],
};

const MANIFEST_KINDS = new Set(["hls", "mux", "dash"]);

/* Packaged Electron is file:// and cannot identify itself to YouTube. */
const YOUTUBE_EMBED_ORIGIN = "https://app.closedcaptioncreator.com";

/* The poster is forwarded to the embed host, which cannot reach app-relative
   or local URLs; Cloudflare stops loading entirely when given one. */
const EMBED_KINDS = new Set(["youtube", "vimeo", "cloudflare"]);

function getYouTubeEmbedOrigin() {
    if (typeof window !== "undefined" && /^https?:$/i.test(window.location.protocol)) {
        return window.location.origin;
    }
    return YOUTUBE_EMBED_ORIGIN;
}

function applyPoster(el, elKind, poster) {
    if (!poster || EMBED_KINDS.has(elKind) || !("poster" in el)) return;
    el.poster = poster;
}

function normalizeSource(value) {
    if (typeof value === "string") {
        return value ? { src: value, type: "" } : null;
    }
    if (value && typeof value === "object" && value.src) {
        return { src: String(value.src), type: value.type || "" };
    }
    return null;
}

function buildHlsConfig() {
    const hlsConfig = {};
    if (externalDataState.idToken && externalDataState.slug) {
        hlsConfig.xhrSetup = (xhr) => {
            xhr.setRequestHeader("Authorization", externalDataState.idToken);
            xhr.setRequestHeader("x-organization-slug", externalDataState.slug);
        };
    }
    if (projectState?.media?.storage === MEDIA_SOURCES.PROXY_RT) {
        /* Proxy RT segments are transcoded just-in-time: a seek into
           an unencoded region blocks the request while FFmpeg
           restarts at that position, so allow slow first bytes
           instead of treating them as fatal load errors. */
        const patientRetry = {
            maxNumRetry: 6,
            retryDelayMs: 1000,
            maxRetryDelayMs: 8000,
        };
        hlsConfig.fragLoadPolicy = {
            default: {
                maxTimeToFirstByteMs: 120000,
                maxLoadTimeMs: 180000,
                timeoutRetry: patientRetry,
                errorRetry: patientRetry,
            },
        };
    }
    return hlsConfig;
}

function clampRate(kind, rate) {
    const limits = RATE_LIMITS[kind];
    if (!limits) return rate;
    return Math.min(limits[1], Math.max(limits[0], rate));
}

export function createVideojsPlayerAdapter() {
    const events = new EventTarget();
    const subscribers = new Set();

    /** @type {HTMLElement|null} */
    let playerEl = null;
    /** @type {any} */
    let mediaEl = null;
    let mediaElKind = null;
    let mediaReady = false;
    let detachMediaListeners = null;
    let unsubscribeStore = null;

    let source = null;
    let kind = null;
    let poster = "";
    let playbackRate = 1;
    let volume = 1;
    let pendingTime = null;
    let playRequested = false;
    let pendingPlay = false;
    let rateCheckTimer = null;

    /* Until MediaPlayer swaps in the element for a new kind, the old element
       still holds the previous source and must not answer for the new one. */
    function activeMedia() {
        return mediaEl && mediaElKind === kind ? mediaEl : null;
    }

    function isPlaying() {
        const el = activeMedia();
        if (!el) return false;
        if (typeof el.paused === "boolean") return !el.paused;
        const storePaused = playerEl?.store?.state?.paused;
        if (typeof storePaused === "boolean") return !storePaused;
        return playRequested;
    }

    function readCurrentTime() {
        const time = Number(activeMedia()?.currentTime);
        if (Number.isFinite(time) && pendingTime === null) return time;
        return pendingTime ?? 0;
    }

    function readDuration() {
        const duration = Number(activeMedia()?.duration);
        return Number.isFinite(duration) && duration > 0 ? duration : 0;
    }

    function snapshot() {
        return {
            playing: isPlaying(),
            paused: !isPlaying(),
            ended: Boolean(activeMedia()?.ended),
            currentTime: readCurrentTime(),
            duration: readDuration(),
        };
    }

    function notify() {
        if (subscribers.size === 0) return;
        const state = snapshot();
        subscribers.forEach((fn) => {
            try {
                fn(state);
            } catch (err) {
                console.error("[VideojsPlayerAdapter] subscriber failed:", err);
            }
        });
    }

    function dispatch(type, detail) {
        events.dispatchEvent(new CustomEvent(type, { detail }));
    }

    function applyRate(el) {
        const rate = clampRate(mediaElKind, playbackRate);
        if (rate !== playbackRate) {
            playbackRate = rate;
            updatePlayerSpeed(rate);
        }
        try {
            if ("defaultPlaybackRate" in el) el.defaultPlaybackRate = rate;
            el.playbackRate = rate;
        } catch (err) {
            console.warn("[VideojsPlayerAdapter] playbackRate rejected:", err);
        }

        /* Some engines silently cap the rate; report what they actually use. */
        clearTimeout(rateCheckTimer);
        rateCheckTimer = setTimeout(() => {
            if (el !== mediaEl || readDuration() === 0) return;
            const actual = Number(el.playbackRate);
            if (Number.isFinite(actual) && actual > 0 && Math.abs(actual - playbackRate) > 0.001) {
                playbackRate = actual;
                updatePlayerSpeed(actual);
            }
        }, 400);
    }

    function applyVolume(el) {
        try {
            el.volume = Math.min(1, Math.max(0, volume));
        } catch (err) {
            console.warn("[VideojsPlayerAdapter] volume rejected:", err);
        }
    }

    function applySource() {
        const el = mediaEl;
        if (!el || !mediaReady || !source || mediaElKind !== kind) return;

        const { src } = source;
        if (kind === "hls") {
            el.source = {
                src,
                type: "application/vnd.apple.mpegurl",
                engine: { hlsJs: buildHlsConfig() },
            };
        } else if (kind === "mux" && !/^https?:/i.test(src)) {
            el.source = { playbackId: src };
        } else if (kind === "youtube") {
            const embedOrigin = getYouTubeEmbedOrigin();
            el.source = {
                src,
                engine: {
                    youtube: {
                        origin: embedOrigin,
                        widget_referrer: embedOrigin,
                    },
                },
            };
        } else {
            el.src = src;
        }

        applyPoster(el, mediaElKind, poster);
        applyRate(el);
        applyVolume(el);
    }

    function bindMediaListeners(el) {
        const handlers = {};
        FORWARDED_EVENTS.forEach((type) => {
            handlers[type] = () => {
                if (type === "loadedmetadata") {
                    if (pendingTime !== null) {
                        const time = pendingTime;
                        pendingTime = null;
                        el.currentTime = time;
                    }
                    applyRate(el);
                    applyVolume(el);
                    if (MANIFEST_KINDS.has(mediaElKind)) dispatch("hls-manifest-loaded");
                }

                if (type === "durationchange") {
                    dispatch(type, readDuration());
                } else if (type === "timeupdate" || type === "seeking" || type === "seeked") {
                    dispatch(type, readCurrentTime());
                } else {
                    dispatch(type, el.error ?? undefined);
                }

                if (type === "play" || type === "playing") playRequested = true;
                if (type === "pause" || type === "ended") playRequested = false;
                notify();
            };
            el.addEventListener(type, handlers[type]);
        });

        return () => {
            FORWARDED_EVENTS.forEach((type) => el.removeEventListener(type, handlers[type]));
        };
    }

    function detachMedia() {
        clearTimeout(rateCheckTimer);
        detachMediaListeners?.();
        detachMediaListeners = null;
        mediaEl = null;
        mediaElKind = null;
        mediaReady = false;
        playRequested = false;
    }

    const adapter = {
        /** Media kind of the current source, or null when nothing is loaded. */
        get kind() {
            return kind;
        },

        get src() {
            return source ? { ...source } : "";
        },
        set src(value) {
            const next = normalizeSource(value);
            source = next;
            kind = next
                ? detectMediaKind(next.src, next.type, projectState?.media?.storage)
                : null;
            pendingTime = null;
            playRequested = false;
            pendingPlay = false;

            if (mediaEl && mediaElKind !== kind) {
                try {
                    mediaEl.pause?.();
                } catch {
                    /* element may already be torn down */
                }
            }

            if (kind && LAZY_MEDIA[kind]) {
                LAZY_MEDIA[kind]().catch((err) => {
                    console.error(`[VideojsPlayerAdapter] failed to load ${kind} media:`, err);
                });
            }

            applySource();
            dispatch("source-change", next ? { kind, ...next } : null);
            notify();
        },

        get currentTime() {
            return readCurrentTime();
        },
        set currentTime(value) {
            const time = Number(value);
            if (!Number.isFinite(time)) return;
            const target = Math.max(0, time);
            const el = activeMedia();
            if (el && mediaReady && readDuration() > 0) {
                el.currentTime = target;
            } else {
                pendingTime = target;
            }
            notify();
        },

        get duration() {
            return readDuration();
        },

        get playbackRate() {
            return playbackRate;
        },
        set playbackRate(value) {
            const rate = Number(value);
            if (!Number.isFinite(rate) || rate <= 0) return;
            playbackRate = rate;
            if (mediaEl && mediaReady) applyRate(mediaEl);
        },

        get volume() {
            return volume;
        },
        set volume(value) {
            const next = Number(value);
            if (!Number.isFinite(next)) return;
            volume = next;
            if (mediaEl && mediaReady) applyVolume(mediaEl);
        },

        get poster() {
            return poster;
        },
        set poster(value) {
            poster = value || "";
            if (mediaEl && mediaReady) applyPoster(mediaEl, mediaElKind, poster);
        },

        get paused() {
            return !isPlaying();
        },
        get playing() {
            return isPlaying();
        },
        get ended() {
            return Boolean(activeMedia()?.ended);
        },

        state: {
            get playing() {
                return isPlaying();
            },
            get paused() {
                return !isPlaying();
            },
            get ended() {
                return Boolean(activeMedia()?.ended);
            },
        },

        async play() {
            const el = activeMedia();
            if (!el || !mediaReady) {
                pendingPlay = Boolean(source);
                return;
            }
            playRequested = true;
            try {
                await el.play();
            } catch (err) {
                playRequested = false;
                console.log("Play failed:", err?.message || err);
                dispatch("play-fail", err);
            }
            notify();
        },

        async pause() {
            pendingPlay = false;
            const el = activeMedia();
            if (!el) return;
            playRequested = false;
            try {
                await el.pause();
            } catch (err) {
                console.log("Pause failed:", err?.message || err);
            }
            notify();
        },

        /** The element that renders frames, for filters and zoom. */
        getVideoElement() {
            const el = activeMedia();
            if (!el) return null;
            if (el.tagName === "VIDEO") return el;
            return el.shadowRoot?.querySelector("video") || el;
        },

        /**
         * @param {(state: { playing: boolean, paused: boolean, ended: boolean, currentTime: number, duration: number }) => void} fn
         * @returns {() => void}
         */
        subscribe(fn) {
            subscribers.add(fn);
            fn(snapshot());
            return () => subscribers.delete(fn);
        },

        addEventListener(type, listener, options) {
            events.addEventListener(type, listener, options);
        },
        removeEventListener(type, listener, options) {
            events.removeEventListener(type, listener, options);
        },

        /** @param {HTMLElement|null} el the `<video-player>` element */
        async attachPlayer(el) {
            unsubscribeStore?.();
            unsubscribeStore = null;
            playerEl = el;
            if (!el) return;
            await customElements.whenDefined("video-player");
            if (playerEl !== el) return;
            unsubscribeStore = el.store?.subscribe?.(() => notify()) || null;
        },

        /**
         * Called by MediaPlayer whenever the rendered media element mounts or swaps.
         * @param {HTMLElement|null} el
         * @param {string|null} elKind
         */
        async attachMedia(el, elKind) {
            if (el === mediaEl) return;
            detachMedia();
            if (!el) {
                notify();
                return;
            }

            mediaEl = el;
            mediaElKind = elKind;
            detachMediaListeners = bindMediaListeners(el);

            const tag = MEDIA_KIND_TAGS[elKind];
            if (tag?.includes("-")) {
                try {
                    await LAZY_MEDIA[elKind]?.();
                    await customElements.whenDefined(tag);
                } catch (err) {
                    console.error(`[VideojsPlayerAdapter] ${tag} is unavailable:`, err);
                    return;
                }
            }
            if (mediaEl !== el) return;

            mediaReady = true;
            applySource();
            if (pendingPlay && mediaElKind === kind) {
                pendingPlay = false;
                adapter.play();
            }
            notify();
        },

        /** Detach `el` if it is still the active media element. */
        releaseMedia(el) {
            if (el !== mediaEl) return;
            detachMedia();
            notify();
        },

        destroy() {
            detachMedia();
            unsubscribeStore?.();
            unsubscribeStore = null;
            playerEl = null;
            subscribers.clear();
        },
    };

    return adapter;
}
